import React from 'react';

import { css } from '@emotion/react';
import { Type } from '@robinhood/design';
import { NeutralColorToken } from '@robinhood/design';
import { graphql } from 'relay-runtime';

import logger from 'src/clients/LoggingClient';
import { VerifySSNPageMutation as VerifySSNPageMutationType } from 'src/clients/Network/gql/build/VerifySSNPageMutation.graphql';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import GetHelpTextButton from 'src/components/creditApplication/GetHelpTextButton';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import { useGlobalStore } from 'src/state/global';
import { useAsyncMutation } from 'src/utils/graphql';
import LoadingPage from 'src/views/creditApplication/LoadingPage';

const VerifySSNPageMutation = graphql`
  mutation VerifySSNPageMutation($creditApplicationId: String!) {
    requestSsnVerification(q: { creditApplicationId: $creditApplicationId }) {
      data {
        ssnVerified
      }
      errors
    }
  }
`;

const VerifySSNPage = () => {
  const { underwriteLoading, underwrite } = React.useContext(NavigationContext);
  const [verifyCommit, verifyLoading] = useAsyncMutation<VerifySSNPageMutationType>(VerifySSNPageMutation);
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const loading = verifyLoading || underwriteLoading;

  const onSubmit = React.useCallback(async () => {
    try {
      if (!!creditApplicationId) {
        await verifyCommit({
          variables: { creditApplicationId },
        });
        await underwrite();
      }
    } catch (e) {
      logger.error(e, { source: 'VerifySSNPage.onSubmit', creditApplicationId });
      await underwrite();
    }
  }, [verifyCommit, creditApplicationId]);

  return loading ? (
    <LoadingPage loadingText='This can take up to 2 minutes. Do not close your browser.' />
  ) : (
    <CreditApplicationPageLayout
      showLogo={false}
      icon='EconomicMoat24'
      title='Verify your Social Security&nbsp;Number'
      subtitle='This automated verification with the Social Security Administration only takes a couple of minutes and helps protect your identity.'
    >
      <div css={root}>
        <div css={disclaimerContainer}>
          <Type.Text size='S' color={NeutralColorToken.Fg2} isBold>
            Authorization for the Social Security Administration to Disclose Your Social Security Number Verification
          </Type.Text>
          <Type.Text size='S' color={NeutralColorToken.Fg2}>
            I authorize the Social Security Administration (SSA) to verify and disclose to Robinhood Credit, Inc.
            through SentiLink Verification Services Corp., their service provider, for the purpose of this transaction
            whether the name, Social Security Number (SSN) and date of birth I have submitted matches information in SSA
            records. My consent is for a one-time validation within the next 7 days. By clicking “Verify SSN” below, you
            are providing legal authorization for SSA to disclose your SSN verification to Robinhood Credit, Inc., which
            authorization has the same effectiveness as your handwritten signature.
          </Type.Text>
        </div>
        <Button label='Verify SSN' state='default' width='full' type='submit' onClick={onSubmit} />
        <GetHelpTextButton />
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 32px;
`;

const disclaimerContainer = css`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 8px;
`;

export default VerifySSNPage;
